import React, { useEffect, useState } from 'react';
import { supabase } from '../supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://replyfirst-production.up.railway.app';

function Voicemails() {
  const [businessId, setBusinessId] = useState(null);
  const [voicemails, setVoicemails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchBusinessId = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: business } = await supabase.from('businesses').select('id').eq('owner_email', user.email).single();
      if (business) setBusinessId(business.id);
    };
    fetchBusinessId();
  }, []);

  useEffect(() => {
    if (!businessId) return;

    const fetchVoicemails = async () => {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        .from('voicemails')
        .select('id, caller_phone, recording_sid, duration, transcription, summary, listened, created_at')
        .eq('business_id', businessId)
        .order('created_at', { ascending: false });

      if (fetchError) {
        setError('Unable to load voicemails. Please try again.');
        setLoading(false);
        return;
      }

      setVoicemails(data || []);
      setLoading(false);
    };

    fetchVoicemails();
  }, [businessId]);

  const markAsListened = async (id) => {
    await supabase.from('voicemails').update({ listened: true }).eq('id', id);
    setVoicemails(prev => prev.map(v => v.id === id ? { ...v, listened: true } : v));
  };

  const formatDateTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F4F7FF', padding: '32px', boxSizing: 'border-box', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div style={{ maxWidth: '1120px', margin: '0 auto' }}>

        <header style={{ marginBottom: '32px' }}>
          <h1 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '32px', fontWeight: 800, color: '#0C1A2E', letterSpacing: '-0.5px' }}>Voicemails</h1>
          <p style={{ marginTop: '6px', color: '#5A6B84', fontSize: '14px' }}>Listen to voicemails and read AI summaries of what each customer needed.</p>
        </header>

        {error && (
          <div style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '10px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', fontSize: '14px' }}>{error}</div>
        )}

        <section style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #DDE3F0', boxShadow: '0 2px 8px rgba(26,86,219,0.06)', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #DDE3F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '18px', fontWeight: 700, color: '#0C1A2E' }}>All Voicemails</h2>
            {loading && <span style={{ fontSize: '12px', color: '#9AABBE' }}>Loading…</span>}
          </div>

          {voicemails.length === 0 && !loading ? (
            <div style={{ padding: '32px 24px', fontSize: '14px', color: '#5A6B84', textAlign: 'center' }}>
              <div style={{ fontSize: '40px', marginBottom: '12px' }}>🎙️</div>
              <div style={{ fontWeight: 600, color: '#0C1A2E', marginBottom: '6px' }}>No voicemails yet</div>
              <div>When customers press 2 and leave a voicemail, it will appear here with a summary.</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {voicemails.map((vm, index) => (
                <div key={vm.id} style={{ padding: '20px 24px', borderBottom: index < voicemails.length - 1 ? '1px solid #EEF2FF' : 'none', backgroundColor: vm.listened ? '#fff' : '#F8FAFF' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {!vm.listened && <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#1A56DB', flexShrink: 0 }} />}
                      <div>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: '#0C1A2E' }}>{vm.caller_phone || 'Unknown'}</div>
                        <div style={{ fontSize: '13px', color: '#5A6B84', marginTop: '2px' }}>
                          {formatDateTime(vm.created_at)}{vm.duration ? ` · ${formatDuration(vm.duration)}` : ''}
                        </div>
                      </div>
                    </div>
                    {vm.caller_phone && (
                      <a href={`tel:${vm.caller_phone}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '7px 14px', borderRadius: '999px', backgroundColor: '#22C55E', color: '#fff', fontSize: '13px', fontWeight: 700, textDecoration: 'none' }}>
                        📞 Call back
                      </a>
                    )}
                  </div>

                  {vm.summary ? (
                    <div style={{ padding: '12px 14px', backgroundColor: '#EEF3FF', borderRadius: '10px', border: '1px solid #C3D2F7', marginBottom: '14px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#1A56DB', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>AI Summary</div>
                      <div style={{ fontSize: '14px', color: '#0C1A2E', lineHeight: 1.6 }}>{vm.summary}</div>
                    </div>
                  ) : vm.transcription ? (
                    <div style={{ padding: '12px 14px', backgroundColor: '#F4F7FF', borderRadius: '10px', border: '1px solid #DDE3F0', marginBottom: '14px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#5A6B84', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>Transcription</div>
                      <div style={{ fontSize: '14px', color: '#3D4F66', lineHeight: 1.6, fontStyle: 'italic' }}>"{vm.transcription}"</div>
                    </div>
                  ) : (
                    <div style={{ padding: '10px 14px', backgroundColor: '#F4F7FF', borderRadius: '8px', marginBottom: '14px', fontSize: '13px', color: '#9AABBE' }}>
                      Summary being generated…
                    </div>
                  )}

                  {vm.recording_sid && (
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#5A6B84', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Listen to voicemail</div>
                      <audio controls onPlay={() => !vm.listened && markAsListened(vm.id)} style={{ width: '100%', height: '40px', outline: 'none' }} src={`${BACKEND_URL}/recording-proxy/${vm.recording_sid}`}>
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default Voicemails;